<template>
  <div class="warehouse-outputs-page p-4">
    <div class="flex flex-wrap items-center justify-between gap-3 mb-4">
      <h3 class="text-xl font-semibold text-blue-900 m-0">
        {{ $t('outputs') }}
      </h3>
      <div class="flex flex-wrap items-center gap-2">

        <b-button
          type="is-primary"
          size="is-small"
          :disabled="isCreateMode"
          @click="onLoadOutbound"
        >
          {{ $t('load_outputs') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          :loading="isExporting"
          :disabled="isExporting || isCreateMode"
          @click="onExport"
        >
          {{ $t('export') }}
        </b-button>
      </div>
    </div>

    <div v-if="isCreateMode" class="p-4 border border-gray-300 rounded bg-white">
      <h4 class="text-base font-semibold text-blue-900 mb-3">{{ isEditingMode ? $t('edit_output') : $t('create_output') }}</h4>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <b-field :label="$t('delivery_number')">
          <b-input :value="form.delivery_number" placeholder="IEM-AAAA-NNNNN" disabled />
        </b-field>
        <b-field :label="$t('subcontractor')">
          <b-select
            v-model="form.subcontractor_id"
            expanded
            :loading="isPartnersLoading"
            :placeholder="$t('select_subcontractor')"
          >
            <option
              v-for="partner in subcontractorOptions"
              :key="partner.id"
              :value="partner.id"
            >
              {{ partner.name || partner.code || `#${partner.id}` }}
            </option>
          </b-select>
        </b-field>
        <b-field :label="$t('received_by')">
          <b-input v-model.trim="form.received_by" maxlength="255" :placeholder="$t('received_by')" />
        </b-field>
      </div>
      <div class="grid grid-cols-1 md:grid-cols-3 gap-3">
        <b-field :label="$t('delivery_date')">
          <b-input v-model="form.delivery_date" type="date" />
        </b-field>
        <b-field :label="$t('total_quantity_label')">
          <b-input :value="String(totalDraftMaterialsQuantity)" disabled />
        </b-field>
        <b-field :label="$t('description')" class="md:col-span-1">
          <b-input v-model.trim="form.description" maxlength="500" type="textarea" />
        </b-field>
      </div>

      <div class="mt-4 p-3 border border-gray-300 rounded warehouse-outputs-create-tables">
        <h5 class="text-base font-semibold text-blue-900 mb-3">{{ $t('material_search') }}</h5>
        <div class="grid grid-cols-1 md:grid-cols-5 gap-3">
          <b-field :label="$t('material')">
            <warehouse-catalog-autocomplete
              v-model="inventoryFilters.warehouse_material_id"
              :project-id="normalizedProjectId"
              endpoint="materials"
              response-key="materials"
              order-field="code"
              :placeholder="$t('material')"
              :formatter="formatMaterialOption"
              @input="onFiltersChange"
            />
          </b-field>

          <b-field :label="$t('provider')">
            <warehouse-catalog-autocomplete
              v-model="inventoryFilters.warehouse_provider_id"
              :project-id="normalizedProjectId"
              endpoint="providers"
              response-key="providers"
              order-field="name"
              :placeholder="$t('provider')"
              :formatter="formatProviderOption"
              @input="onFiltersChange"
            />
          </b-field>

          <b-field :label="$t('category')">
            <b-select
              v-model="inventoryFilters.category"
              expanded
              :loading="isCategoriesLoading"
              :placeholder="$t('category')"
              @input="onFiltersChange"
            >
              <option :value="null">{{ $t('all') }}</option>
              <option
                v-for="cat in categoryOptions"
                :key="`cat-${cat.id}`"
                :value="cat.name"
              >
                {{ cat.name }}
              </option>
            </b-select>
          </b-field>

          <b-field :label="$t('order_number')">
            <b-input
              v-model.trim="inventoryFilters.material_order_number"
              :placeholder="$t('order_number')"
              @input="onFiltersChangeDebounced"
            />
          </b-field>

          <b-field :label="$t('search')">
            <b-input
              v-model.trim="inventoryFilters.search"
              :placeholder="$t('warehouse_outputs_inventory_search_placeholder')"
              @input="onFiltersChangeDebounced"
            />
          </b-field>
        </div>

        <div class="mt-3 relative">
          <b-loading :active="isInventoryListLoading" :is-full-page="false" />
          <b-table
            :data="inventoryListRows"
            :row-class="inventoryRowClass"
            striped
            bordered
            narrowed
            mobile-cards
            icon-pack="fas"
          >
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" sortable v-slot="props">
              {{ props.row.unique_material_id || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" sortable v-slot="props">
              {{ props.row.description || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" sortable v-slot="props">
              {{ props.row.provider || '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" sortable v-slot="props">
              {{ props.row.unit || '-' }}
            </b-table-column>
            <b-table-column field="drawing_number" :label="$t('drawing_number')" sortable v-slot="props">
              {{ props.row.drawing_number || '-' }}
            </b-table-column>
            <b-table-column field="tag_kks" :label="$t('tag_kks')" sortable v-slot="props">
              {{ props.row.tag_kks || '-' }}
            </b-table-column>
            <b-table-column field="quantity" :label="$t('stock')" numeric sortable v-slot="props">
              {{ props.row.quantity != null ? props.row.quantity : '-' }}
            </b-table-column>
            <b-table-column
              field="remaining"
              :label="$t('stock_after_output')"
              numeric
              sortable
              :custom-sort="sortByPending"
              v-slot="props"
            >
              <span :class="pendingCellClass(props.row)">
                {{ pendingForRow(props.row) }}
              </span>
            </b-table-column>
            <b-table-column field="draft_qty" :label="$t('quantity')" numeric v-slot="props">
              <b-input
                :value="inventoryDraftQtyById[props.row.id] != null ? String(inventoryDraftQtyById[props.row.id]) : ''"
                type="number"
                min="0"
                :max="props.row.quantity != null ? props.row.quantity : undefined"
                size="is-small"
                :disabled="isRowExhausted(props.row)"
                @input="(val) => onInventoryRowQtyInput(props.row, val)"
              />
            </b-table-column>
            <b-table-column field="actions" :label="$t('actions')" centered v-slot="props">
              <b-button
                type="is-primary"
                size="is-small"
                :disabled="!canAddInventoryRow(props.row)"
                @click="() => onAddInventoryRow(props.row)"
              >
                {{ $t('add') }}
              </b-button>
            </b-table-column>
          </b-table>

          <p v-if="!isInventoryListLoading && inventoryListRows.length === 0" class="text-sm text-gray-600 m-0 py-2">
            {{ $t('no_results') }}
          </p>
        </div>

        <div class="mt-3">
          <b-table
            :data="form.items"
            striped
            bordered
            mobile-cards
            icon-pack="fas"
          >
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" sortable v-slot="props">
              {{ props.row.unique_material_id || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" sortable v-slot="props">
              {{ props.row.description || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" sortable v-slot="props">
              {{ props.row.provider || '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" sortable v-slot="props">
              {{ props.row.unit || '-' }}
            </b-table-column>
            <b-table-column field="drawing_number" :label="$t('drawing_number')" sortable v-slot="props">
              {{ props.row.drawing_number || '-' }}
            </b-table-column>
            <b-table-column field="tag_kks" :label="$t('tag_kks')" sortable v-slot="props">
              {{ props.row.tag_kks || '-' }}
            </b-table-column>
            <b-table-column field="quantity" :label="$t('quantity')" numeric sortable v-slot="props">
              {{ props.row.quantity != null ? props.row.quantity : '-' }}
            </b-table-column>
            <b-table-column field="actions" :label="$t('actions')" centered v-slot="props">
              <b-button type="is-danger" size="is-small" outlined @click="onRemoveMaterialLine(props.index)">
                {{ $t('delete') }}
              </b-button>
            </b-table-column>
          </b-table>
        </div>
      </div>
      <div class="mt-3 flex items-center justify-end gap-2">
        <b-button size="is-small" @click="onCancelCreateOutbound">
          {{ $t('cancel') }}
        </b-button>
        <b-button
          type="is-primary"
          size="is-small"
          :loading="isSavingOutbound"
          @click="onSaveCreateOutbound"
        >
          {{ $t('save') }}
        </b-button>
      </div>
    </div>

    <div v-else class="max-w-full overflow-x-auto">
      <b-collapse
        class="card filters-collapse mb-3"
        v-model="showListFilters"
      >
        <template #trigger>
          <div class="card-header filters-trigger">
            <p class="card-header-title">
              {{ $t('filters') }}
            </p>
            <a class="card-header-icon">
              <b-icon pack="fa" v-show="!showListFilters" icon="plus-circle" />
              <b-icon pack="fa" v-show="showListFilters" icon="minus-circle" />
            </a>
          </div>
        </template>

        <div class="panel" style="border-width: 0">
          <div class="grid grid-cols-1 md:grid-cols-4 gap-3">
            <b-field :label="$t('subcontractor')">
              <b-select
                v-model="listFilters.partner_id"
                expanded
                :loading="isPartnersLoading"
                :disabled="isPartnersLoading"
                :placeholder="$t('subcontractor')"
                @input="onListFiltersChange"
              >
                <option :value="null">{{ $t('all') }}</option>
                <option
                  v-for="partner in subcontractorOptions"
                  :key="`lf-pt-${partner.id}`"
                  :value="partner.id"
                >
                  {{ partner.name || partner.code || `#${partner.id}` }}
                </option>
              </b-select>
            </b-field>

            <b-field :label="$t('date_start')">
              <b-input
                v-model="listFilters.delivery_date_from"
                type="date"
                @input="onListFiltersChange"
              />
            </b-field>

            <b-field :label="$t('date_end')">
              <b-input
                v-model="listFilters.delivery_date_to"
                type="date"
                @input="onListFiltersChange"
              />
            </b-field>

            <b-field :label="$t('search')">
              <b-input
                v-model.trim="listFilters.search"
                :placeholder="$t('search')"
                @input="onListFiltersChangeDebounced"
              />
            </b-field>
          </div>
        </div>
      </b-collapse>

      <b-table
        :data="rows"
        :loading="isLoading"
        detailed
        detail-key="id"
        :show-detail-icon="true"
        :has-detailed-visible="() => true"
        paginated
        :per-page="25"
        striped
        bordered
        mobile-cards
        icon-pack="fas"
      >
      <!--
      <b-table-column
        field="id"
        :label="$t('id')"
        width="90"
        v-slot="props"
      >
        {{ props.row.id != null ? props.row.id : '-' }}
      </b-table-column>
      -->

      <b-table-column
        field="delivery_number"
        :label="$t('delivery_number')"
        width="150"
        sortable
        v-slot="props"
      >
        {{ props.row.delivery_number || '-' }}
      </b-table-column>

      <b-table-column
        field="delivery_date"
        :label="$t('delivery_date')"
        width="140"
        sortable
        v-slot="props"
      >
        {{ formatDeliveryDate(props.row.delivery_date) }}
      </b-table-column>

      <b-table-column field="description" :label="$t('description')" sortable v-slot="props">
        {{ props.row.description || '-' }}
      </b-table-column>

      <b-table-column
        field="subcontractor"
        :label="$t('subcontractor')"
        width="160"
        sortable
        v-slot="props"
      >
        {{ props.row.subcontractor || '-' }}
      </b-table-column>

      <b-table-column
        field="received_by"
        :label="$t('received_by')"
        sortable
        v-slot="props"
      >
        {{ props.row.received_by || '-' }}
      </b-table-column>

      <b-table-column
        field="elements_count"
        :label="$t('warehouse_outbound_elements_count')"
        width="160"
        numeric
        sortable
        :custom-sort="sortByElementsCount"
        v-slot="props"
      >
        {{ outboundDetailLineCount(props.row) }}
      </b-table-column>

      <b-table-column
        field="actions"
        :label="$t('actions')"
        centered
        width="120"
        v-slot="props"
      >
        <div class="w-full h-full flex items-center justify-center gap-3">
          <b-tooltip :label="$t('edit')" position="is-top">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-gray-600"
              @click="onEditOutboundRow(props.row)"
              @keydown.enter.prevent="onEditOutboundRow(props.row)"
            >
              <b-icon pack="fas" icon="pencil-alt" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('export')" position="is-top">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-gray-600"
              :class="{ 'opacity-50 pointer-events-none': isExporting }"
              @click="onExportIemReport(props.row)"
              @keydown.enter.prevent="onExportIemReport(props.row)"
            >
              <b-icon pack="fas" icon="file-alt" />
            </div>
          </b-tooltip>
          <b-tooltip :label="$t('delete')" position="is-top" type="is-danger">
            <div
              role="button"
              tabindex="0"
              class="cursor-pointer text-red-600"
              :class="{ 'opacity-50 pointer-events-none': deletingOutboundId === props.row.id }"
              @click="onDeleteOutbound(props.row)"
              @keydown.enter.prevent="onDeleteOutbound(props.row)"
            >
              <b-icon pack="fas" icon="trash-alt" />
            </div>
          </b-tooltip>
        </div>
      </b-table-column>

      <template #detail="props">
        <div class="pl-5 pr-2 pb-4 bg-gray-50">
          <h4 class="text-sm font-semibold text-blue-900 mb-2">
            {{ $t('line_items') }}
          </h4>
          <b-table
            v-if="outboundDetailRows(props.row).length"
            :data="outboundDetailRows(props.row)"
            striped
            bordered
            narrowed
            mobile-cards
            icon-pack="fas"
          >
            <b-table-column field="unique_material_id" :label="$t('unique_material_id')" sortable v-slot="line">
              {{ line.row.unique_material_id || '-' }}
            </b-table-column>
            <b-table-column field="description" :label="$t('description')" sortable v-slot="line">
              {{ line.row.description || '-' }}
            </b-table-column>
            <b-table-column field="provider" :label="$t('provider')" sortable v-slot="line">
              {{ line.row.provider || '-' }}
            </b-table-column>
            <b-table-column field="order_number" :label="$t('order_number')" width="130" sortable v-slot="line">
              {{ line.row.order_number || '-' }}
            </b-table-column>
            <b-table-column field="quantity" :label="$t('quantity')" numeric width="100" sortable v-slot="line">
              {{ line.row.quantity != null ? line.row.quantity : '-' }}
            </b-table-column>
            <b-table-column field="unit" :label="$t('unit')" width="110" sortable v-slot="line">
              {{ line.row.unit || '-' }}
            </b-table-column>
            <b-table-column field="drawing_number" :label="$t('drawing_number')" width="150" sortable v-slot="line">
              {{ line.row.drawing_number || '-' }}
            </b-table-column>
            <b-table-column field="tag_kks" :label="$t('tag_kks')" width="150" sortable v-slot="line">
              {{ line.row.tag_kks || '-' }}
            </b-table-column>
            <b-table-column
              field="activity_id"
              :label="`${$t('id')} ${$t('activity')}`"
              width="130"
              sortable
              :custom-sort="sortByOutboundLineActivity"
              v-slot="line"
            >
              {{ formatOutboundLineActivity(line.row) }}
            </b-table-column>
          </b-table>
          <p v-else class="text-sm text-gray-600 m-0 py-2">
            {{ $t('no_results') }}
          </p>
        </div>
      </template>
      </b-table>
    </div>
  </div>
</template>

<script>
import axios from 'axios'
import { mapGetters } from 'vuex'
import WarehouseCatalogAutocomplete from './warehouse-catalog-autocomplete.vue'

export default {
  name: 'warehouse-outputs',
  components: {
    WarehouseCatalogAutocomplete,
  },
  props: {
    projectId: {
      type: [String, Number],
      required: true,
    },
  },
  data: () => ({
    isLoading: false,
    isPartnersLoading: false,
    isSavingOutbound: false,
    isExporting: false,
    isCreateMode: false,
    editingOutboundId: null,
    deletingOutboundId: null,
    rows: [],
    showListFilters: false,
    listFilters: {
      partner_id: null,
      delivery_date_from: '',
      delivery_date_to: '',
      search: '',
    },
    listFiltersDebounce: null,
    subcontractorOptions: [],
    form: {
      delivery_number: '',
      delivery_date: '',
      subcontractor_id: null,
      received_by: '',
      description: '',
      items: [],
    },
    materialOptions: [],
    providerOptions: [],
    isCatalogsLoading: false,
    categoryOptions: [],
    isCategoriesLoading: false,
    inventoryFilters: {
      warehouse_material_id: null,
      warehouse_provider_id: null,
      category: null,
      material_order_number: '',
      search: '',
    },
    inventoryListRows: [],
    isInventoryListLoading: false,
    inventoryListDebounce: null,
    /** cantidad borrador por id de inventario */
    inventoryDraftQtyById: {},
  }),
  computed: {
    ...mapGetters({
      company: 'app/company',
    }),
    normalizedProjectId() {
      return Number(this.projectId) || null
    },
    isEditingMode() {
      return this.editingOutboundId != null
    },
    totalDraftMaterialsQuantity() {
      if (!Array.isArray(this.form.items)) return 0
      return this.form.items.reduce((sum, row) => {
        const q = row && row.quantity != null ? Number(row.quantity) : 0
        return sum + (Number.isFinite(q) && q > 0 ? Math.floor(q) : 0)
      }, 0)
    },
    normalizedWarehouseMaterialId() {
      const v = this.inventoryFilters && this.inventoryFilters.warehouse_material_id
      const n = v != null && v !== '' ? Number(v) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
    normalizedWarehouseProviderId() {
      const v = this.inventoryFilters && this.inventoryFilters.warehouse_provider_id
      const n = v != null && v !== '' ? Number(v) : null
      return Number.isFinite(n) && n > 0 ? n : null
    },
  },
  created() {
    this.loadSubcontractors()
    this.loadRows()
    this.loadCatalogs()
    this.loadCategories()
  },
  watch: {
    projectId() {
      this.loadSubcontractors()
      this.loadRows()
      this.loadCatalogs()
      this.loadCategories()
    },
  },
  methods: {
    formatMaterialOption(m) {
      if (!m) return ''
      return `${m.code || '-'} - ${m.description || '-'}`
    },
    formatProviderOption(p) {
      if (!p) return ''
      return (p.code ? `${p.code} - ` : '') + (p.name || '-')
    },
    extractArrayFromResponse(data, key) {
      if (Array.isArray(data && data[key])) return data[key]
      if (Array.isArray(data && data.data && data.data[key])) return data.data[key]
      return []
    },
    async loadCatalogs() {
      if (!this.normalizedProjectId) {
        this.materialOptions = []
        this.providerOptions = []
        return
      }
      this.isCatalogsLoading = true
      try {
        const [materialsRes, providersRes] = await Promise.all([
          axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/materials`, {
            params: { per_page: 100, page: 1, order_field: 'code', order_dir: 'asc' },
          }),
          axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/providers`, {
            params: { per_page: 100, page: 1, order_field: 'name', order_dir: 'asc' },
          }),
        ])
        this.materialOptions = this.extractArrayFromResponse(materialsRes.data, 'materials')
        this.providerOptions = this.extractArrayFromResponse(providersRes.data, 'providers')
      } catch (e) {
        this.materialOptions = []
        this.providerOptions = []
      } finally {
        this.isCatalogsLoading = false
      }
    },
    async loadCategories() {
      if (!this.normalizedProjectId) {
        this.categoryOptions = []
        return
      }
      this.isCategoriesLoading = true
      try {
        const { data } = await axios.get(
          `/api/v2/project/${this.normalizedProjectId}/warehouse/taxonomies`,
          { params: { type: 'category', per_page: 200, page: 1 } }
        )
        const list = (data && Array.isArray(data.taxonomies)) ? data.taxonomies : []
        this.categoryOptions = list
          .map((t) => ({ id: t.id, name: (t.name || '').toString().trim() }))
          .filter((t) => t.name !== '')
      } catch (e) {
        this.categoryOptions = []
      } finally {
        this.isCategoriesLoading = false
      }
    },
    sortByPending(a, b, isAsc) {
      const va = this.pendingForRow(a)
      const vb = this.pendingForRow(b)
      if (va === vb) return 0
      return isAsc ? va - vb : vb - va
    },
    sortByElementsCount(a, b, isAsc) {
      const va = this.outboundDetailLineCount(a)
      const vb = this.outboundDetailLineCount(b)
      if (va === vb) return 0
      return isAsc ? va - vb : vb - va
    },
    sortByOutboundLineActivity(a, b, isAsc) {
      const va = this.formatOutboundLineActivity(a)
      const vb = this.formatOutboundLineActivity(b)
      if (va === vb) return 0
      return isAsc ? String(va).localeCompare(String(vb)) : String(vb).localeCompare(String(va))
    },
    async loadSubcontractors() {
      if (!this.normalizedProjectId) {
        this.subcontractorOptions = []
        return
      }
      this.isPartnersLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/partners`, {
          params: { per_page: 100, page: 1 },
        })
        const partners = (data && Array.isArray(data.data)) ? data.data : []
        this.subcontractorOptions = partners
      } catch (e) {
        this.subcontractorOptions = []
      } finally {
        this.isPartnersLoading = false
      }
    },
    async loadRows() {
      if (!this.normalizedProjectId) {
        this.rows = []
        return
      }
      this.isLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/outbounds`, {
          params: {
            per_page: 50,
            page: 1,
            order_field: 'id',
            order_dir: 'desc',
            ...this.listFilterParams(),
          },
        })
        this.rows = (data && data.success && Array.isArray(data.outbounds)) ? data.outbounds : []
      } catch (e) {
        this.rows = []
      } finally {
        this.isLoading = false
      }
    },
    listFilterParams() {
      const f = this.listFilters || {}
      const search = f.search ? String(f.search).trim() : ''
      const partnerId = f.partner_id != null && f.partner_id !== '' ? Number(f.partner_id) : null
      return {
        partner_id: Number.isFinite(partnerId) && partnerId > 0 ? partnerId : undefined,
        delivery_date_from: f.delivery_date_from || undefined,
        delivery_date_to: f.delivery_date_to || undefined,
        search: search !== '' ? search : undefined,
      }
    },
    onListFiltersChange() {
      this.loadRows()
    },
    onListFiltersChangeDebounced() {
      if (this.listFiltersDebounce) clearTimeout(this.listFiltersDebounce)
      this.listFiltersDebounce = setTimeout(() => {
        this.loadRows()
      }, 350)
    },
    formatDeliveryDate(value) {
      if (value == null || value === '') return '-'
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}/.test(value)) {
        const d = new Date(value)
        if (!Number.isNaN(d.getTime())) {
          return d.toLocaleDateString('es-ES', {
            day: 'numeric',
            month: 'numeric',
            year: 'numeric',
          })
        }
      }
      return String(value)
    },
    outboundDetailRows(row) {
      if (!row || !Array.isArray(row.items)) return []
      return row.items
    },
    outboundDetailLineCount(row) {
      return this.outboundDetailRows(row).length
    },
    formatOutboundLineActivity(lineRow) {
      if (!lineRow) return '-'
      const code = lineRow.activity_planning_code != null ? String(lineRow.activity_planning_code).trim() : ''
      if (code !== '') return code
      return lineRow.activity_id != null ? lineRow.activity_id : '-'
    },
    onLoadOutbound() {
      this.isCreateMode = true
    },
    onFiltersChange() {
      this.loadInventoryList()
    },
    onFiltersChangeDebounced() {
      if (this.inventoryListDebounce) clearTimeout(this.inventoryListDebounce)
      this.inventoryListDebounce = setTimeout(() => {
        this.loadInventoryList()
      }, 220)
    },
    async loadInventoryList() {
      if (!this.normalizedProjectId) {
        this.inventoryListRows = []
        return
      }
      const search = this.inventoryFilters.search ? String(this.inventoryFilters.search).trim() : ''
      const orderNumber = this.inventoryFilters.material_order_number
        ? String(this.inventoryFilters.material_order_number).trim()
        : ''
      const category = this.inventoryFilters.category
        ? String(this.inventoryFilters.category).trim()
        : ''
      this.isInventoryListLoading = true
      try {
        const { data } = await axios.get(`/api/v2/project/${this.normalizedProjectId}/warehouse/inventory`, {
          params: {
            per_page: 30,
            page: 1,
            order_field: 'id',
            order_dir: 'desc',
            with_bulk: 0,
            warehouse_material_id: this.normalizedWarehouseMaterialId || undefined,
            warehouse_provider_id: this.normalizedWarehouseProviderId || undefined,
            category: category !== '' ? category : undefined,
            material_order_number: orderNumber !== '' ? orderNumber : undefined,
            search: search !== '' ? search : undefined,
            description: search !== '' ? search : undefined,
          },
        })
        const rows = (data && data.success && Array.isArray(data.inventory)) ? data.inventory : []
        this.inventoryListRows = rows
      } catch (e) {
        this.inventoryListRows = []
      } finally {
        this.isInventoryListLoading = false
      }
    },
    inventoryRowClass(row) {
      return this.isRowExhausted(row) ? 'warehouse-outputs-row-exhausted' : ''
    },
    addedQtyForRow(row) {
      const invId = row && row.id != null ? Number(row.id) : null
      if (!Number.isFinite(invId)) return 0
      const inForm = (this.form.items || []).find((it) => it && Number(it.inventory_id) === invId)
      return inForm ? Number(inForm.quantity) || 0 : 0
    },
    effectiveQtyForRow(row) {
      const invId = row && row.id != null ? Number(row.id) : null
      if (!Number.isFinite(invId)) return 0
      const draft = this.inventoryDraftQtyById[invId]
      if (draft != null && draft !== '') {
        const n = Number(draft)
        return Number.isFinite(n) ? n : 0
      }
      return this.addedQtyForRow(row)
    },
    pendingForRow(row) {
      const total = row && row.quantity != null ? Number(row.quantity) : 0
      const pending = total - this.effectiveQtyForRow(row)
      return pending < 0 ? 0 : pending
    },
    isRowExhausted(row) {
      if (!row || row.quantity == null) return false
      return this.effectiveQtyForRow(row) >= Number(row.quantity)
    },
    pendingCellClass(row) {
      if (!row || row.quantity == null) return ''
      const total = Number(row.quantity)
      const effective = this.effectiveQtyForRow(row)
      if (effective > total) return 'has-text-danger has-text-weight-bold'
      if (effective > 0) return 'has-text-success has-text-weight-bold'
      return 'has-text-grey-light'
    },
    onInventoryRowQtyInput(row, raw) {
      const invId = row && row.id != null ? Number(row.id) : null
      if (!Number.isFinite(invId)) return
      if (raw === '' || raw == null) {
        this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [invId]: null }
        return
      }
      let n = Number(raw)
      if (!Number.isFinite(n)) {
        this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [invId]: null }
        return
      }
      if (n < 0) n = 0
      const max = row && row.quantity != null ? Number(row.quantity) : null
      if (max != null && Number.isFinite(max) && n > max) n = max
      this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [invId]: n }
    },
    canAddInventoryRow(row) {
      if (!row || row.id == null) return false
      const invId = Number(row.id)
      if (!Number.isFinite(invId)) return false
      const qty = this.inventoryDraftQtyById[invId]
      const q = qty != null ? Number(qty) : NaN
      if (!Number.isFinite(q) || q < 1) return false
      const available = row.quantity != null ? Number(row.quantity) : null
      if (available != null && Number.isFinite(available) && q > available) return false
      return true
    },
    onAddInventoryRow(row) {
      if (!this.canAddInventoryRow(row)) {
        this.$toast.open({
          message: this.$t('enter_valid_quantity_int_min_1'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }
      const invId = Number(row.id)
      const quantity = Math.floor(Number(this.inventoryDraftQtyById[invId]))
      const line = {
        inventory_id: invId,
        unique_material_id: row.unique_material_id ? String(row.unique_material_id).trim() : '',
        description: row.description ? String(row.description).trim() : '',
        provider: row.provider ? String(row.provider).trim() : '',
        unit: row.unit ? String(row.unit).trim() : '',
        drawing_number: row.drawing_number ? String(row.drawing_number).trim() : '',
        tag_kks: row.tag_kks ? String(row.tag_kks).trim() : '',
        quantity,
      }
      const items = Array.isArray(this.form.items) ? [...this.form.items] : []
      const existingIdx = items.findIndex((it) => it && Number(it.inventory_id) === invId)
      if (existingIdx !== -1) {
        items[existingIdx] = line
        this.form.items = items
      } else {
        this.form.items = [...items, line]
      }
      this.inventoryDraftQtyById = { ...this.inventoryDraftQtyById, [invId]: null }
    },
    onRemoveMaterialLine(index) {
      if (!Array.isArray(this.form.items)) return
      this.form.items = this.form.items.filter((_, i) => i !== index)
    },
    resetForm() {
      this.form = {
        delivery_number: '',
        delivery_date: '',
        subcontractor_id: null,
        received_by: '',
        description: '',
        items: [],
      }
      this.inventoryFilters = {
        warehouse_material_id: null,
        warehouse_provider_id: null,
        category: null,
        material_order_number: '',
        search: '',
      }
      this.inventoryListRows = []
      this.inventoryDraftQtyById = {}
      this.editingOutboundId = null
    },
    onEditOutboundRow(row) {
      if (!row || row.id == null) return
      this.resetForm()
      this.editingOutboundId = Number(row.id)
      this.form = {
        delivery_number: row.delivery_number ? String(row.delivery_number) : '',
        delivery_date: row.delivery_date ? String(row.delivery_date).slice(0, 10) : '',
        subcontractor_id: row.partner_id != null ? Number(row.partner_id) : null,
        received_by: row.received_by ? String(row.received_by) : '',
        description: row.description ? String(row.description) : '',
        items: Array.isArray(row.items)
          ? row.items
            .filter((it) => it && it.warehouse_inventory_id != null)
            .map((it) => ({
              inventory_id: Number(it.warehouse_inventory_id),
              unique_material_id: it.unique_material_id || '',
              description: it.description || '',
              provider: it.provider || '',
              unit: it.unit || '',
              drawing_number: it.drawing_number || '',
              tag_kks: it.tag_kks || '',
              quantity: Number(it.quantity) || 0,
            }))
          : [],
      }
      this.isCreateMode = true
    },
    onCancelCreateOutbound() {
      this.resetForm()
      this.isCreateMode = false
    },
    firstValidationMessage(errorData) {
      const err = errorData && errorData.errors
      if (err && typeof err === 'object') {
        const firstKey = Object.keys(err)[0]
        if (firstKey && Array.isArray(err[firstKey]) && err[firstKey].length) {
          return err[firstKey][0]
        }
      }
      if (errorData && errorData.message) {
        return errorData.message
      }
      return null
    },
    async onSaveCreateOutbound() {
      if (!this.normalizedProjectId) return
      if (!this.form.subcontractor_id) {
        this.$toast.open({
          message: this.$t('select_subcontractor'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }
      if (!this.form.delivery_date) {
        this.$toast.open({
          message: this.$t('delivery_date'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }
      if (!Array.isArray(this.form.items) || this.form.items.length === 0) {
        this.$toast.open({
          message: this.$t('add_at_least_one_line'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }

      const payload = {
        description: this.form.description ? String(this.form.description).trim() : '',
        partner_id: Number(this.form.subcontractor_id),
        received_by: this.form.received_by ? String(this.form.received_by).trim() : '',
        delivery_date: this.form.delivery_date,
        items: [],
      }

      for (const row of this.form.items) {
        if (!row || !row.inventory_id) continue
        const q = Number(row.quantity)
        if (!Number.isFinite(q) || q < 1) {
          this.$toast.open({
            message: this.$t('line_quantity_min_1'),
            type: 'is-warning',
            position: 'is-top-right',
          })
          return
        }
        payload.items.push({
          warehouse_inventory_id: Number(row.inventory_id),
          quantity: q,
        })
      }

      if (payload.items.length === 0) {
        this.$toast.open({
          message: this.$t('add_at_least_one_line'),
          type: 'is-warning',
          position: 'is-top-right',
        })
        return
      }

      this.isSavingOutbound = true
      try {
        const isEditing = this.editingOutboundId != null
        const url = isEditing
          ? `/api/v2/project/${this.normalizedProjectId}/warehouse/outbounds/${this.editingOutboundId}`
          : `/api/v2/project/${this.normalizedProjectId}/warehouse/outbounds`
        const { data } = isEditing
          ? await axios.put(url, payload)
          : await axios.post(url, payload)
        if (data && data.success) {
          this.$toast.open({
            message: this.$t('save_successful'),
            type: 'is-success',
            position: 'is-top-right',
          })
          this.resetForm()
          this.isCreateMode = false
          this.loadRows()
          return
        }
        const msg = this.firstValidationMessage(data) || this.$t('error_saving')
        this.$toast.open({
          message: msg,
          type: 'is-danger',
          position: 'is-top-right',
        })
      } catch (e) {
        const resp = e.response && e.response.data
        const msg = this.firstValidationMessage(resp) || (e.message ? String(e.message) : '') || this.$t('error_saving')
        console.error('[warehouse-outputs] save outbound failed', { status: e.response && e.response.status, response: resp, error: e })
        this.$toast.open({
          message: msg,
          type: 'is-danger',
          position: 'is-top-right',
        })
      } finally {
        this.isSavingOutbound = false
      }
    },
    onDeleteOutbound(row) {
      if (!row || row.id == null) return
      const outboundId = Number(row.id)
      const label = row.delivery_number ? String(row.delivery_number) : String(outboundId)
      this.$buefy.dialog.confirm({
        title: this.$t('delete'),
        message: this.$t('delete_outbound_confirm_text', [label]),
        confirmText: this.$t('delete'),
        cancelText: this.$t('cancel'),
        type: 'is-danger',
        hasIcon: true,
        onConfirm: async () => {
          this.deletingOutboundId = outboundId
          try {
            const { data } = await axios.delete(
              `/api/v2/project/${this.normalizedProjectId}/warehouse/outbounds/${outboundId}`
            )
            if (data && data.success) {
              this.$toast.open({
                message: this.$t('deleted_successfully'),
                type: 'is-success',
                position: 'is-top-right',
              })
              this.loadRows()
            } else {
              const msg = this.firstValidationMessage(data) || this.$t('error')
              this.$toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
            }
          } catch (e) {
            const resp = e.response && e.response.data
            const msg = this.firstValidationMessage(resp) || (e.message ? String(e.message) : '') || this.$t('error')
            console.error('[warehouse-outputs] delete outbound failed', { status: e.response && e.response.status, response: resp, error: e })
            this.$toast.open({ message: msg, type: 'is-danger', position: 'is-top-right' })
          } finally {
            this.deletingOutboundId = null
          }
        },
      })
    },
    onExport() {
      return this.onExportIemReport()
    },
    async onExportIemReport(row = null) {
      if (!this.normalizedProjectId) return
      const outboundId = row && row.id != null ? Number(row.id) : null
      const isSingle = Number.isFinite(outboundId) && outboundId >= 1
      const filters = { project_id: this.normalizedProjectId }
      if (isSingle) filters.warehouse_outbound_id = outboundId

      this.isExporting = true
      try {
        const res = await axios.post(
          '/api/v2/export/warehouse-delivery-report',
          { filters },
          { responseType: 'blob' }
        )
        const contentDisposition =
          (res && res.headers && (res.headers['content-disposition'] || res.headers['Content-Disposition'])) || ''
        const match = /filename\*?=(?:UTF-8''|")?([^;"\n]+)/i.exec(contentDisposition)
        const fallbackName = 'Informe_IEM_Entrega.xlsx'
        const filename =
          (match && match[1] ? decodeURIComponent(match[1].replace(/"/g, '').trim()) : '') || fallbackName

        const blob = new Blob([res.data], {
          type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
        })
        const url = window.URL.createObjectURL(blob)
        const a = document.createElement('a')
        a.href = url
        a.download = filename
        document.body.appendChild(a)
        a.click()
        a.remove()
        window.URL.revokeObjectURL(url)
      } catch (e) {
        try {
          const msg = (e && e.response && e.response.data && e.response.data.message) || (e && e.message) || this.$t('error')
          if (this.$buefy && this.$buefy.toast && this.$buefy.toast.open) {
            this.$buefy.toast.open({ message: msg, type: 'is-danger' })
          }
        } catch (_) {
          // ignore toast rendering errors
        }
        console.error(e)
      } finally {
        this.isExporting = false
      }
    },
  },
}
</script>

<style scoped>
/* Fila con todo el stock ya reservado en el borrador de salida. */
::v-deep .warehouse-outputs-row-exhausted {
  opacity: 0.55;
}

/* Las dos tablas del formulario de "Crear salida" deben ajustarse al ancho del
   contenedor sin generar scroll horizontal: table-layout fixed + truncado. */
.warehouse-outputs-create-tables {
  overflow-x: hidden;
}
.warehouse-outputs-create-tables ::v-deep .table-wrapper {
  overflow-x: hidden;
  max-width: 100%;
}
.warehouse-outputs-create-tables ::v-deep .b-table .table {
  table-layout: fixed;
  width: 100%;
}
.warehouse-outputs-create-tables ::v-deep .b-table .table td,
.warehouse-outputs-create-tables ::v-deep .b-table .table th {
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  word-break: break-word;
}
.warehouse-outputs-create-tables ::v-deep .b-table .table td .input {
  min-width: 0;
}
</style>
